import { useState } from "react";
import ErrorMessages from "../../../components/ErrorMessages/ErrorMessage";
import DocumentUpload, {
  type PendingDocument,
  uploadPendingDocuments,
} from "../../../components/Files/DocumentUpload";
import { FormTextArea } from "../../../components/Input/FormTextArea";
import { LoadingSpinner } from "../../../components/Spinner/LoadingSpinner";
import type { TaskProps } from "../../../types/TaskProps";
import { CommonUserTaskNew } from "../CommonUserTaskNew";
import type { ComponentConfig } from "../types";

const UploadDocument = (props: TaskProps) => {
  const { task } = props;

  const [documents, setDocuments] = useState<PendingDocument[]>(
    task?.data?.documents ?? task?.context?.caseworkerDocuments ?? [],
  );
  const [comment, setComment] = useState<string>(task?.data?.comment ?? "");
  const [errorMessages, setErrorMessages] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Files are only uploaded to Flow file storage here, on submit — not while
  // the caseworker is still selecting them.
  const submit = async (action: "trigger" | "save") => {
    if (action === "trigger" && documents.length < 1) {
      setErrorMessages("Please upload at least one document.");
      return;
    }
    setErrorMessages("");
    setIsSubmitting(true);

    let resolved: PendingDocument[];
    try {
      resolved = await uploadPendingDocuments(documents);
    } catch (_error) {
      setIsSubmitting(false);
      setErrorMessages(
        "Could not upload the selected documents. Please try again.",
      );
      return;
    }
    setDocuments(resolved);

    const formData = { documents: resolved, comment };
    const onError = (e: unknown) => {
      setIsSubmitting(false);
      setErrorMessages((e as { detail?: string })?.detail ?? "");
    };

    if (action === "trigger") {
      props.trigger(formData, () => setIsSubmitting(false), onError);
    } else {
      props.save(formData, () => setIsSubmitting(false), onError, true);
    }
  };

  const formConfig: ComponentConfig[] = [
    {
      component: (
        <div style={{ padding: "0 28px" }}>
          <p style={{ margin: "0 0 0.5rem 0", color: "#666" }}>
            Upload supporting documentation for this application. Files are
            stored on the application, but are not sent to Core.
          </p>
        </div>
      ),
      order: 1,
    },
    {
      component: (
        <DocumentUpload
          value={documents}
          onChange={(next) => {
            setErrorMessages("");
            setDocuments(next);
          }}
          disabled={isSubmitting}
        />
      ),
      order: 2,
    },
    {
      component: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "0 28px",
            color: "#666",
          }}
        >
          <LoadingSpinner width={20} height={20} />
          <span>Uploading…</span>
        </div>
      ),
      order: 3,
      hide: !isSubmitting,
    },
    {
      component: (
        <FormTextArea
          name="Comment"
          onChange={(e: string) => setComment(e)}
          label="Comment"
          defaultValue={comment || ""}
          placeholder="Enter your comment here (optional)"
        />
      ),
      order: 4,
    },
    {
      component: (
        <div style={{ padding: "0 28px" }}>
          <ErrorMessages errors={[errorMessages]} />
        </div>
      ),
      order: 5,
      hide: errorMessages === "",
    },
  ];

  return (
    <CommonUserTaskNew
      handleComplete={() => submit("trigger")}
      handleSave={() => submit("save")}
      formConfig={formConfig}
      formWidth={480}
      disableButtons={isSubmitting}
    />
  );
};

export default UploadDocument;
